// src/components/dashboard/TimeOverlapReport.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Chip,
  TextField,
  Select,
  MenuItem,
  Button,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from "@mui/material";

// Guaranteed MUI Icons
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";

const TimeOverlapReport = () => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkTheme = () => {
      const darkActive =
        document.documentElement.classList.contains("dark-theme") ||
        document.body.classList.contains("dark-theme");
      setIsDark(darkActive);
    };

    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });

    return () => observer.disconnect();
  }, []);

  // Filter States
  const [quickRange, setQuickRange] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [excludeNonBillable, setExcludeNonBillable] = useState(false);

  // Pagination & Exact Dataset States
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  const [overlapRows] = useState([
    {
      id: 1,
      appointmentDate: "7/21/2026",
      client: 19,
      firstName: "Test",
      lastName: "Patient",
      staff1: "Ali Ammar",
      session1: "08:00 - 12:15",
      status1: "",
      auditReady1: "",
      code1: "",
      staff2: "Atif",
      session2: "08:00 - 16:04",
      status2: "",
      auditReady2: "",
      code2: "",
    },
    {
      id: 2,
      appointmentDate: "7/21/2026",
      client: 20,
      firstName: "Test",
      lastName: "Ahmad",
      staff1: "TruEvv",
      session1: "08:45 - 10:15",
      status1: "",
      auditReady1: "",
      code1: "",
      staff2: "Ali Ammar",
      session2: "08:00 - 12:15",
      status2: "",
      auditReady2: "",
      code2: "",
    },
    {
      id: 3,
      appointmentDate: "7/21/2026",
      client: 20,
      firstName: "Test",
      lastName: "Ahmad",
      staff1: "TruEvv",
      session1: "08:45 - 10:15",
      status1: "",
      auditReady1: "",
      code1: "",
      staff2: "Atif",
      session2: "08:00 - 16:04",
      status2: "",
      auditReady2: "",
      code2: "",
    },
    {
      id: 4,
      appointmentDate: "7/28/2026",
      client: 19,
      firstName: "Test",
      lastName: "Patient",
      staff1: "Ali Ammar",
      session1: "08:00 - 12:15",
      status1: "",
      auditReady1: "",
      code1: "",
      staff2: "Atif",
      session2: "08:00 - 16:04",
      status2: "",
      auditReady2: "",
      code2: "",
    },
    {
      id: 5,
      appointmentDate: "8/4/2026",
      client: 19,
      firstName: "Test",
      lastName: "Patient",
      staff1: "Ali Ammar",
      session1: "08:00 - 12:15",
      status1: "",
      auditReady1: "",
      code1: "",
      staff2: "Atif",
      session2: "08:00 - 16:04",
      status2: "",
      auditReady2: "",
      code2: "",
    },
  ]);

  // Clinical Palette Tokens
  const tealMain = "#1E5E5F";
  const tealButton = "#245E65";
  const labelTeal = "#276A6C";
  const pastelTealBg = "#D9EBEF";
  const cardBg = isDark ? "#0F172A" : "#FFFFFF";
  const cardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#245E65";
  const softCardBorder = isDark ? "rgba(255, 255, 255, 0.08)" : "#EAEFF4";
  const primaryText = isDark ? "#F8FAFC" : "#0A2540";
  const secondaryText = isDark ? "#94A3B8" : "#64748B";
  const controlBg = isDark ? "rgba(255, 255, 255, 0.03)" : "#FFFFFF";
  const controlBorder = isDark ? "rgba(255, 255, 255, 0.12)" : "#CBD5E1";
  const dropdownBg = isDark ? "#1E293B" : "#FFFFFF";

  const handleClear = () => {
    setQuickRange("");
    setDateFrom("");
    setDateTo("");
    setExcludeNonBillable(false);
  };

  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: dropdownBg,
        border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#B8D6DD"}`,
        borderRadius: "8px",
        color: primaryText,
        boxShadow: isDark ? "0 12px 32px rgba(0, 0, 0, 0.6)" : "0 8px 24px rgba(15, 23, 42, 0.08)",
        "& .MuiMenuItem-root": {
          fontSize: "0.82rem",
          py: 0.8,
          "&:hover": { backgroundColor: isDark ? "rgba(255, 255, 255, 0.06)" : pastelTealBg },
          "&.Mui-selected": { backgroundColor: isDark ? "rgba(30, 94, 95, 0.35)" : pastelTealBg, color: tealButton, fontWeight: 700 },
        },
      },
    },
  };

  return (
    <Box sx={{ width: "100%", pb: 6 }}>
      {/* 1. Header Banner */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.2, md: "20px 26px" },
          display: "flex",
          alignItems: "center",
          gap: 2,
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          mb: 3,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "14px",
            backgroundColor: tealMain,
            color: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <AccessTimeRoundedIcon sx={{ fontSize: "1.75rem" }} />
        </Box>

        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
            <Typography sx={{ fontSize: { xs: "1.2rem", md: "1.4rem" }, fontWeight: 800, color: primaryText }}>
              Time Overlap Report
            </Typography>
            <Chip
              label="Audits & Conflicts"
              size="small"
              sx={{
                height: "22px",
                fontSize: "0.68rem",
                fontWeight: 700,
                backgroundColor: pastelTealBg,
                color: tealButton,
                borderRadius: "6px",
              }}
            />
          </Box>
          <Typography sx={{ fontSize: "0.82rem", color: secondaryText, fontWeight: 500, mt: 0.3 }}>
            Identify scheduling conflicts and overlapping appointments
          </Typography>
        </Box>
      </Box>

      {/* 2. Filter Time Overlaps Section */}
      <Box
        sx={{
          backgroundColor: cardBg,
          borderRadius: "20px",
          border: `1.5px solid ${cardBorder}`,
          p: { xs: 2.5, md: "26px 30px" },
          boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 2px 10px rgba(10, 37, 64, 0.02)",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <SearchIcon sx={{ color: labelTeal, fontSize: "1.3rem" }} />
          <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color: primaryText }}>
            Filter Time Overlaps
          </Typography>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2.5 }}>
          {/* Quick Range */}
          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: labelTeal, mb: 0.8 }}>Quick Range</Typography>
            <Select
              fullWidth
              displayEmpty
              size="small"
              value={quickRange}
              onChange={(e) => setQuickRange(e.target.value)}
              IconComponent={KeyboardArrowDownRoundedIcon}
              MenuProps={menuProps}
              renderValue={(selected) => (selected ? selected : <Typography sx={{ color: secondaryText, fontSize: "0.85rem" }}>Select date range...</Typography>)}
              sx={{ borderRadius: "8px", backgroundColor: controlBg, color: primaryText, fontSize: "0.85rem", "& fieldset": { borderColor: controlBorder } }}
            >
              <MenuItem value="Today">Today</MenuItem>
              <MenuItem value="This Week">This Week</MenuItem>
              <MenuItem value="This Month">This Month</MenuItem>
            </Select>
          </Box>

          {/* Date of Session: from to */}
          <Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: labelTeal, mb: 0.8 }}>Date of Session:</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", backgroundColor: controlBg, color: primaryText, fontSize: "0.85rem", "& fieldset": { borderColor: controlBorder } } }}
              />
              <Typography sx={{ fontSize: "0.82rem", color: secondaryText, fontWeight: 600 }}>to</Typography>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", backgroundColor: controlBg, color: primaryText, fontSize: "0.85rem", "& fieldset": { borderColor: controlBorder } } }}
              />
            </Box>
          </Box>
        </Box>

        {/* Exclude Non-Billable Checkbox */}
        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={excludeNonBillable}
                onChange={(e) => setExcludeNonBillable(e.target.checked)}
                sx={{ color: tealButton, "&.Mui-checked": { color: tealMain } }}
              />
            }
            label={<Typography sx={{ fontSize: "0.84rem", fontWeight: 600, color: primaryText }}>Exclude Non-Billable</Typography>}
          />
        </Box>

        {/* Buttons */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3 }}>
          <Button
            variant="contained"
            startIcon={<SearchIcon sx={{ fontSize: "1.1rem !important" }} />}
            sx={{
              height: "40px",
              backgroundColor: tealMain,
              color: "#FFFFFF",
              fontWeight: 700,
              fontSize: "0.84rem",
              borderRadius: "8px",
              px: 3,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { backgroundColor: tealButton, boxShadow: "none" },
            }}
          >
            Search
          </Button>

          <Button
            variant="outlined"
            onClick={handleClear}
            startIcon={<DeleteOutlineRoundedIcon sx={{ fontSize: "1.1rem !important" }} />}
            sx={{
              height: "40px",
              borderColor: tealButton,
              color: tealButton,
              fontWeight: 700,
              fontSize: "0.84rem",
              borderRadius: "8px",
              px: 2.8,
              textTransform: "none",
              "&:hover": { borderColor: tealMain, backgroundColor: "rgba(36, 94, 101, 0.05)" },
            }}
          >
            Clear
          </Button>
        </Box>
      </Box>

      {/* 3. Results Table (Wide Multi-Column Container) */}
      <Box sx={{ backgroundColor: cardBg, borderRadius: "16px", border: `1px solid ${softCardBorder}`, overflow: "hidden" }}>
        <TableContainer
          sx={{
            minHeight: "360px",
            overflowX: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Table stickyHeader sx={{ minWidth: 1200 }}>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    color: primaryText,
                    fontWeight: 800,
                    fontSize: "0.82rem",
                    backgroundColor: isDark ? "#162238" : "#F8FAFC",
                    py: 1.8,
                    px: 2,
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <TableCell>Appointment Date</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>First Name</TableCell>
                <TableCell>Last Name</TableCell>
                <TableCell>Staff 1</TableCell>
                <TableCell>Session 1</TableCell>
                <TableCell>Status 1</TableCell>
                <TableCell>Audit Ready 1</TableCell>
                <TableCell>Code 1</TableCell>
                <TableCell>Staff 2</TableCell>
                <TableCell>Session 2</TableCell>
                <TableCell>Status 2</TableCell>
                <TableCell>Audit Ready 2</TableCell>
                <TableCell>Code 2</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {overlapRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    "&:hover": { backgroundColor: isDark ? "rgba(30, 94, 95, 0.08)" : "rgba(36, 94, 101, 0.03)" },
                    "& td": { py: 1.8, px: 2, fontSize: "0.82rem", whiteSpace: "nowrap" },
                  }}
                >
                  <TableCell sx={{ color: primaryText, fontWeight: 500 }}>{row.appointmentDate}</TableCell>
                  <TableCell sx={{ color: primaryText, fontWeight: 600 }}>{row.client}</TableCell>
                  <TableCell sx={{ color: secondaryText }}>{row.firstName}</TableCell>
                  <TableCell sx={{ color: primaryText }}>{row.lastName}</TableCell>
                  <TableCell sx={{ color: primaryText }}>{row.staff1}</TableCell>
                  <TableCell sx={{ color: secondaryText }}>{row.session1}</TableCell>
                  <TableCell sx={{ color: secondaryText }}>{row.status1}</TableCell>
                  <TableCell sx={{ color: secondaryText }}>{row.auditReady1}</TableCell>
                  <TableCell sx={{ color: secondaryText }}>{row.code1}</TableCell>
                  <TableCell sx={{ color: primaryText }}>{row.staff2}</TableCell>
                  <TableCell sx={{ color: secondaryText }}>{row.session2}</TableCell>
                  <TableCell sx={{ color: secondaryText }}>{row.status2}</TableCell>
                  <TableCell sx={{ color: secondaryText }}>{row.auditReady2}</TableCell>
                  <TableCell sx={{ color: secondaryText }}>{row.code2}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={overlapRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          sx={{ borderTop: `1px solid ${softCardBorder}`, color: secondaryText }}
        />
      </Box>
    </Box>
  );
};

export default TimeOverlapReport;